<template>
  <div
    class="product"
    :class="{ 'dealer-mode': dealerMode, selected: productIsTemporal }"
  >
    <ModalAlert
      v-if="showAlertModal"
      :text="'Error trying to save the product in your locker'"
      :title="'Error'"
      :isError="true"
      @closeModal="handleCloseAlertModal"
    />
    <Loader v-if="loading" />
    <template v-else>
      <!-- Product Actions -->

      <ProductActions
        v-if="!anonymousCrossOrigin"
        v-show="
          productIsCustomizable &&
          isTemporalProductEmpty &&
          !isCreateMode &&
          !productIsEmpty &&
          !showAddNewProductButton &&
          !isPlaybookAndUATemplate &&
          !readOnly
        "
        :productActionsCentered="productActionsCentered"
        :product="originalProduct"
        :threekitDesignName="threekitDesignName"
        :productStyleName="productStyleId"
        :layout="finalLayout"
        :isPlaceholderPage="isPlaceholderPage"
        :isUniqueProductInPage="isUniqueProductInPage"
        :columnIndex="columnIndex"
        :itemIndex="itemIndex"
        :itemsCount="itemsCount"
        :sportName="sportName"
        :layoutIndex="layoutIndex"
        :pageId="pageId"
        :pageType="pageType"
        :isPlaybookDesignPage="isPlaybookDesignPage"
        @openSearchModal="emitOpenSearchModal()"
      />

      <!-- Add to locker -->
      <AddToLockerButton
        v-if="showAddToLockerButton && !anonymousCrossOrigin"
        :isProjectLocked="isProjectLocked"
        :dealerMode="dealerMode"
        @addToLocker="handleAddToLocker"
      />

      <!-- Product -->
      <template v-if="!productIsEmpty">
        <!-- Product Brand -->
        <img
          v-if="showBrand && originalProduct && productBrandingIconIsValid"
          class="product-brand"
          :src="getImgUrl()"
        />

        <!-- Product Image -->
        <div
          class="product-preview"
          :class="{
            'full-height': !withDescription && threekitLayers,
            snapsheet: isSnapsheet,
          }"
          v-if="withImage"
        >
          <Loader
            v-if="
              isProductInThreekit && (!threekitLayers || !threekitLayers.length)
            "
          />
          <ProductDisplay
            :customPreview="customPreview"
            :product="originalProduct"
            :threekitDesignName="threekitDesignName"
            :inheritedDesign="activeDesign"
            :layout="finalLayout"
            :precoloredDesign="precoloredDesign"
            :precoloredDesignShadow="precoloredDesignShadow"
            :designDimension="designDimension"
            :precoloredType="precoloredType"
            :isChromagear="isChromagear"
            :isThreekitAvailable="true"
            :anonymousCrossOrigin="anonymousCrossOrigin"
            :featuredInfoType="featuredInfoType"
            :hasFeaturedInfo="hasFeaturedInfo"
            :threekitLayers="threekitLayers"
            :readOnly="readOnly"
            :forcedLocationName="forcedLocationName"
          />
        </div>
        <!-- Info -->
        <ProductInfo
          v-if="!hideInfo"
          :product="originalProduct"
          :productIsEditable="priceIsEditable || productIsEditable"
          :withDescription="withDescription"
          :withProductGrid="withProductGrid"
          :activeGender="activeGender"
          :itemsCount="itemsCount"
          :isPlaceholderPage="isPlaceholderPage"
          :pageType="pageType"
          :pageId="pageId"
          :columnIndex="columnIndex"
          :itemIndex="itemIndex"
          :sportName="sportName"
          :layoutIndex="layoutIndex"
          :anonymousCrossOrigin="anonymousCrossOrigin"
          :isFeatured="isFeatured"
          :nameOverride="nameOverride"
          :heroDesign="heroDesign"
          :showShowroomPrice="showShowroomPrice"
          @setActiveGender="handleSetActiveGender"
          @updating="loading = true"
          @updated="endLoading()"
        />
        <!-- Buttons to save or cancel when the user added a new product manually -->
        <div
          v-if="productIsTemporal && !readOnly"
          class="save-cancel-container"
        >
          <button @click="removeTemporalProduct()" class="btn cancel">
            cancel
          </button>
          <button @click="handleSaveNewProduct()" class="btn save">
            <img
              src="@/assets/icons/ui/add-circle-outline.svg"
              alt="add icon"
            />
            Add Product
          </button>
        </div>
      </template>

      <!-- Add new product button-->
      <button
        v-if="showAddNewProductButton"
        class="btn add"
        @click="emitOpenSearchModal()"
      >
        <img src="@/assets/icons/ui/add-circle-outline.svg" alt="add icon" />
        <span> Add Product</span>
      </button>
    </template>
  </div>
</template>

<script>
import AddToLockerButton from "@/components/Projects/Snapsheets/AddToLockerButton.vue";
import Loader from "@/components/Utils/Loader.vue";
import ModalAlert from "@/components/Utils/ModalAlert.vue";
import ProductDisplay from "@/components/Projects/Product/ProductDisplay/ProductDisplay.vue";
import ProductInfo from "@/components/Projects/Product/ProductInfo.vue";
import ProductActions from "@/components/Projects/Product/ProductActions.vue";
import { mapActions, mapGetters, mapState, mapMutations } from "vuex";
import CONSTANTS from "@/constants";

export default {
  name: "Product",

  data() {
    return {
      waiting: true,
      loading: false,
      showAlertModal: false,
      selectedActiveDesign: null,
      locationType: "",
      activeGender: "",
    };
  },

  computed: {
    ...mapState("auth", ["dealerMode"]),
    ...mapGetters("lockerModule", ["lockerCode"]),
    ...mapState("project", ["temporalProduct"]),
    ...mapGetters("project", [
      "isSnapsheet",
      "isPlaybook",
      "isShowroom",
      "isCreateMode",
      "isEditMode",
      "isPreviewMode",
      "isTemporalProductEmpty",
      "projectColors",
    ]),
    ...mapGetters("actionsPermissions", ["isProjectLocked"]),
    ...mapGetters("playbook", ["isUATemplate"]),

    ...mapGetters("threekit", ["get2DGroupedLayers"]),

    ...mapState("threekit", ["sublimationColors", "cachedTKData"]),

    activeDesign() {
      return this.isSnapsheet || this.isPlaybook
        ? this.originalProduct?.design
        : this.selectedActiveDesign != null
        ? this.selectedActiveDesign
        : this.firstDesignByGender;
    },

    activeProductByGender() {
      if (!this.activeGender) return this.originalProduct;

      return this.originalProduct?.related?.find(
        (related) => related?.gender === this.activeGender
      );
    },

    firstDesignByGender() {
      return this.designsByGender != null ? this.designsByGender[0] : null;
    },

    customPreview() {
      return this.originalProduct?.threekitSavedPreview;
    },

    designsByGender() {
      if (this.isShowroom) {
        const gender = this.originalProduct?.gender
          ? this.originalProduct?.gender.toLowerCase()
          : false;
        if (gender) {
          return this.originalProduct?.designs_by_gender
            ? this.originalProduct?.designs_by_gender[gender]
            : [];
        }
      }
      return [];
    },

    isProductInThreekit() {
      return this.cachedTKData.products?.[this.productStyleId];
    },

    isPlaybookAndUATemplate() {
      return this.isPlaybook && this.isUATemplate;
    },

    isSublimated() {
      return (
        this.originalProduct?.is_sublimable ||
        this.originalProduct?.isSublimable ||
        Object.keys(this.originalProduct)?.find((k) => k?.includes("design"))
      );
    },

    finalLayout() {
      if (this.isSnapsheet) {
        //Find active layout, or use the first one. (Some products do not have a layout)
        const activeLayout =
          this.originalProduct?.layouts?.find(
            (l) => l.id == this.originalProduct?.activeLayoutId
          ) ||
          (this.originalProduct?.layouts && this.originalProduct?.layouts[0]);
        return activeLayout?.layout_attributes?.front;
      }
      //dd
      return this.layoutOverride
        ? this.layoutOverride
        : this.originalProduct?.layout
        ? this.originalProduct?.layout
        : this.layout;
    },

    productBrandingIconIsValid() {
      return (
        this.productBrand === "chromagear" ||
        this.productBrand === "badger" ||
        this.productBrand === "alleson" ||
        this.productBrand === "c2" ||
        this.productBrand === "badger-chromagear"
      );
    },

    productBrand() {
      return this.originalProduct?.brand?.replace(/\s+/g, "-")?.toLowerCase();
    },

    productBrandRoute() {
      const imageUrl = `@/assets/branding/${this.productBrand}.png`;
      return imageUrl;
    },

    productIsEmpty() {
      return this.originalProduct == null || !this.productStyleId;
    },

    productStyleId() {
      return (
        this.activeProductByGender?.style_id ||
        this.activeProductByGender?.styleId ||
        this.activeProductByGender?.style
      );
    },

    productIsEditable() {
      if (this.isSnapsheet) return !this.productIsTemporal;
      //if is not underarmour and...
      //TODO editable or customizable?
      return !this.readOnly;
    },

    productIsTemporal() {
      return (
        !this.isTemporalProductEmpty &&
        this.temporalProduct?.order === this.originalProduct?.order
      );
    },

    showProductInfo() {
      if (this.isSnapsheet || this.isShowroom) return true;
      return this.itemsCount < 3; //3 rows do not show grid
    },

    showAddNewProductButton() {
      if (this.readOnly) return false;

      //Only show it if the product is empty, and there is no product waiting to be added
      if (
        this.productIsEmpty &&
        this.isTemporalProductEmpty &&
        this.dealerMode
      ) {
        return true;
      } else {
        return false;
      }
    },

    showAddToLockerButton() {
      return false;
      // return (
      //   !this.isSnapsheet &&
      //   !this.isShowroom &&
      //   !this.productIsTemporal &&
      //   !this.productIsEmpty &&
      //   !this.readOnly &&
      //   !this.isPlaceholderPage &&
      //   (this.dealerMode ? this.isPreviewMode : this.isPreviewMode) &&
      //   (this.isPlaybook
      //     ? this.pageType?.toLowerCase() === "products" ||
      //       this.pageType?.toLowerCase() === "designs"
      //     : true)
      // );
    },

    threekitDesignName() {
      return (
        this.designName ||
        this.originalProduct?.threekit_designs?.[0]?.design_name
      );
    },

    threekitLayers() {
      if (this.waiting) return []
      if (
        this.threekitDesignName &&
        this.sublimationColors?.length > 0 &&
        this.isSublimated
      ) {
        const colors = {
          color1: this.projectColors[0]?.color?.chromagear_name,
          color2: this.projectColors[1]?.color?.chromagear_name,
          color3: this.projectColors[2]?.color?.chromagear_name,
        };

        return this.get2DGroupedLayers({
          styleName: this.productStyleId,
          designName: this.threekitDesignName,
          ...colors,
        });
      } else {
        return this.get2DGroupedLayers({
          styleName: this.productStyleId,
          color1: this.projectColors[0]?.color?.stock_name,
        });
      }
    },
  },

  async mounted() {
    //I think it looks better when switching pages
    setTimeout(() => {
      this.waiting = false;
    }, 250 * this.itemIndex);
    if (this.isShowroom) {
      this.locationType = await this.getConfigurationByLocationName(
        this.originalProduct?.threekit_layout
      );

      this.locationType = this.locationType?.Type;
    }

    this.activeGender = this.originalProduct?.related?.[0]?.gender;

    this.initProductData();
  },

  methods: {
    ...mapActions("project", ["removeTemporalProduct", "saveNewProduct"]),
    ...mapActions("project", ["addToLocker", "updateProjectStatus"]),
    ...mapMutations("project", ["TOGGLE_LOCKER_PREVIEW_MODAL"]),
    ...mapActions("threekit", [
      "getConfigurationByLocationName",
      "initProductCache",
    ]),

    getImgUrl() {
      return "/assets/branding/" + this.productBrand + ".png";
    },
    async handleAddToLocker() {
      this.loading = true;
      try {
        //Layout-> for sublimables
        await this.addToLocker({
          product: this.originalProduct,
          layout: this.finalLayout,
          isPrecolored:
            !!this.precoloredDesignShadow || !!this.precoloredDesign,
          precoloredDesign: this.precoloredDesign,
          precoloredShadow: this.precoloredDesignShadow,
          isChromagear: this.isChromagear,
          designDimension: this.designDimension,
        });

        //Update project status to 'Open Locker'
        await this.updateProjectStatus(CONSTANTS.PROJECT_STATUS.OPEN_LOCKER);

        this.wasAddedToLocker = true;
        this.TOGGLE_LOCKER_PREVIEW_MODAL();
      } catch (error) {
        console.log(error);
        this.showAlertModal = true;
      } finally {
        this.loading = false;
      }
    },

    handleCloseAlertModal() {
      this.showAlertModal = false;
    },

    async handleSaveNewProduct() {
      try {
        this.loading = true;
        await this.saveNewProduct();
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    handleSetActiveGender(newGender) {
      this.activeGender = newGender;
    },

    async initProductData() {
      await this.initProductCache({
        styleName: this.productStyleId,
        designName: this.threekitDesignName,
      });

      //let relateds = this.originalProduct?.related;

      /*if (relateds?.length < 2) {
        return null;
      } else {
        relateds = relateds?.slice(1);

        relateds?.map((r) => {
          this.initProductCache({
            styleName: r?.style_id,
            designName: this.threekitDesignName,
          });
        });
      }*/
    },

    emitOpenSearchModal() {
      this.$emit("openSearchModal", this.originalProduct);
    },

    emitOpenPreviewNewLockerProducts() {
      this.$emit("openPreviewNewLockerProducts");
    },

    endLoading() {
      this.loading = false;
    },
  },

  props: {
    originalProduct: {
      type: Object,
      required: true,
    },
    productActionsCentered: {
      type: Boolean,
      default: false,
    },
    layout: {
      type: Object,
    },
    withDescription: {
      type: Boolean,
    },
    isPlaybookDesignPage: {
      type: Boolean,
    },
    withProductGrid: {
      type: Boolean,
    },
    hideInfo: {
      type: Boolean,
    },
    withImage: {
      type: Boolean,
    },
    pageType: {
      type: String,
    },
    priceIsEditable: {
      type: Boolean,
      default: false,
    },
    productIsCustomizable: {
      type: Boolean,
      default: false,
    },
    itemsCount: {
      type: Number,
    },
    readOnly: {
      type: Boolean,
    },
    isPlaceholderPage: {
      type: Boolean,
    },
    isUniqueProductInPage: {
      Type: Boolean,
    },
    pageId: {
      Type: Number,
    },
    columnIndex: {
      Type: Number,
    },
    itemIndex: {
      Type: Number,
    },
    precoloredDesign: {
      Type: String,
    },
    designDimension: {
      type: Object,
    },
    designName: {
      type: String,
    },
    precoloredDesignShadow: {
      type: String,
    },
    layoutOverride: {
      type: Object,
    },
    sportName: {
      type: String,
    },
    layoutIndex: {
      type: Number,
    },
    precoloredType: {
      type: String,
    },
    isChromagear: {
      type: Boolean,
    },
    isThreekitAvailable: {
      type: Boolean,
      default: true,
    },
    forcedLocationName: {
      type: String,
    },
    anonymousCrossOrigin: {
      type: Boolean,
    },
    isFeatured: {
      type: Boolean,
    },
    hasFeaturedInfo: {
      type: Boolean,
    },
    featuredInfoType: {
      type: String,
    },
    nameOverride: {
      type: String,
    },
    heroDesign: {
      type: String,
    },
    showBrand: {
      type: Boolean,
    },
    showShowroomPrice: {
      type: Boolean,
    },
    
  },

  components: {
    AddToLockerButton,
    Loader,
    ModalAlert,
    ProductDisplay,
    ProductInfo,
    ProductActions,
  },

  watch: {
    originalProduct() {
      this.activeGender = this.originalProduct?.related?.[0]?.gender;
    },
  },
};
</script>

<style lang="scss" scoped>

#app {
  .page-hero {
    .playbook-page-column-item {
      .product {
        padding: 1em;
      }
    }
  }
  .product {
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
    width: 100%;
    overflow: visible;
    position: relative;
    transition: all 0.2s;
    &.mirrored {
      flex-direction: column-reverse;
    }

    &.dealer-mode {
      position: relative;
    }

    &.selected {
      background: $grey-lightest;
    }
    &:hover {
      //background: $grey-lightest;
    }

    .product-brand {
      max-height: 1.25rem;
      margin-right: auto;
      margin-top: 1rem;
      margin-left: 1rem;
    }

    .product-preview {
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      align-items: center;
      height: 50%;
      width: 100%;
      overflow: hidden;
      &.full-height {
        height: 100%;
      }
      &.snapsheet {
        justify-content: flex-start;
      }
    }

    .save-cancel-container {
      background: #00000029;
      display: flex;
      justify-content: center;
      padding: 0.3rem;
      position: absolute;
      bottom: 0;
      width: 100%;
      .btn {
        text-transform: capitalize;
        display: flex;
        align-items: center;
        img {
          margin-right: 0.5rem;
        }
        &.cancel {
          margin-right: 1rem;
          background: $black;
          color: white;
        }
        &.save {
          background: $green;
          color: white;
        }
      }
    }

    .btn.add {
      display: flex;
      align-items: center;
      background: $dark;
      color: white;
      margin: auto;
      text-transform: capitalize;
      width: 30%;
      span {
        margin: auto;
        color: white;
      }
    }

    ::v-deep .edit-item-indicator {
      font-family: Helvetica, Arial, sans-serif;
      background: $snapsheetColor;
      display: flex;
      align-items: center;
      color: #fff;
      position: absolute;
      height: 30px;
      padding: 0.75rem;
      position: absolute;
      text-decoration: none;

      &:hover {
        background-color: $snapsheetColor;
      }

      &:after {
        display: inline-block;
        border: 15px solid;
        height: 0;
        width: 0;
        position: absolute;
        top: 0;
        content: "";
        display: inline-block;
      }

      &.left {
        left: -7rem;
        border-top-left-radius: 6px;
        border-bottom-left-radius: 6px;
        &:after {
          right: -30px;
          border-color: transparent transparent transparent $snapsheetColor;
        }
      }

      &.right {
        right: -7rem;
        border-top-right-radius: 6px;
        border-bottom-right-radius: 6px;
        &:after {
          left: -30px;
          border-color: transparent $snapsheetColor transparent transparent;
        }
      }
    }
  }
  .product-container {
    .product-preview {
      height: 25em !important;
      justify-content: center;
    }
  }

  #designs-modal {
    .product-preview {
      height: 21em !important;
    }
  }
}
</style>
